import { createCtx } from '@shared/util/context';

/**
 * Files chosen somewhere other than Capture, waiting for the Capture modal to pick them up.
 *
 * A `File` cannot travel in a URL, and Capture is reached by navigating — so a drop that opens
 * Capture has to leave its files somewhere the modal can find them once it mounts. Deliberately not
 * an `AskAttachment`: staging and uploading belong to `useAskAttachmentUpload`, which needs an
 * attachment store, and the store in question is the modal's own. The producer hands over the raw
 * files and the modal puts them through the same pipeline as a drop onto the modal itself, so the
 * content-type policy and the plan limit are still enforced in exactly one place.
 */
export interface CaptureFileHandoff {
  /** Idempotency key: received exactly once, however many times the modal re-renders. */
  id: string;
  /** The workspace whose Capture these files are for. */
  channelSlug: string;
  files: File[];
  /**
   * When these stop being worth receiving, as an epoch millisecond.
   *
   * Capture normally mounts a frame or two after the drop, so this is not a waiting period — it is
   * what stops files from a Capture that never opened (the flag off, a workspace that failed to
   * load) from turning up in the next one the user opens hours later.
   */
  expiresAt: number;
}

export interface CaptureFileHandoffContextValue {
  /** Everything offered and not yet received. */
  handoffs: CaptureFileHandoff[];
  /** Hands files to the Capture for `channelSlug`, whether or not it is mounted yet. */
  offer: (channelSlug: string, files: File[]) => void;
  /** Marks a handoff dealt with, so a later-mounting Capture does not attach the files again. */
  consume: (id: string) => void;
}

/**
 * Two ways in, for the same reasons as `FieldPatchContext`.
 *
 * `useCaptureFileHandoff` throws when the provider is missing, because files offered into nothing
 * are lost silently — the user's drop would open Capture with an empty tray and nothing to explain
 * it. `useReceiveCaptureFiles` reads the context directly and no-ops when it is absent, because a
 * Capture modal rendered without the provider — a unit test, a preview — is not broken, it simply
 * has no files waiting.
 */
export const [useCaptureFileHandoff, CaptureFileHandoffContextProvider, CaptureFileHandoffContext] =
  createCtx<CaptureFileHandoffContextValue>();
